const STORAGE_KEYS = {
  fingerprint: 'device_fingerprint',
  timestamp: 'fingerprint_timestamp',
}

const DEFAULT_MAX_AGE = 7 * 24 * 60 * 60 * 1000

function getStorage() {
  try {
    return window.localStorage
  } catch (error) {
    return null
  }
}

function getNavigatorValue(key, fallback = '') {
  if (typeof navigator === 'undefined') return fallback
  return navigator[key] || fallback
}

function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    if (!context) return ''

    context.textBaseline = 'top'
    context.font = '14px Arial'
    context.fillStyle = '#f60'
    context.fillRect(125, 1, 62, 20)
    context.fillStyle = '#069'
    context.fillText('aqm-fingerprint', 2, 15)
    context.fillStyle = 'rgba(102, 204, 0, 0.7)'
    context.fillText('aqm-fingerprint', 4, 17)

    return canvas.toDataURL()
  } catch (error) {
    return ''
  }
}

function getWebGLFingerprint() {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')

    if (!gl) return ''

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
    if (!debugInfo) return ''

    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || ''
    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || ''

    return `${vendor}::${renderer}`
  } catch (error) {
    return ''
  }
}

async function hashText(text) {
  if (
    typeof window !== 'undefined' &&
    window.crypto &&
    window.crypto.subtle &&
    typeof TextEncoder !== 'undefined'
  ) {
    const encoded = new TextEncoder().encode(text)
    const digest = await window.crypto.subtle.digest('SHA-256', encoded)
    return Array.from(new Uint8Array(digest))
      .map(value => value.toString(16).padStart(2, '0'))
      .join('')
  }

  let hash = 0
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash << 5) - hash + text.charCodeAt(index)
    hash |= 0
  }

  return Math.abs(hash).toString(16)
}

class FingerprintService {
  constructor() {
    this.fingerprintPromise = null
    this.visitorId = null
  }

  collectComponents() {
    const storage = getStorage()

    return {
      userAgent: getNavigatorValue('userAgent'),
      language: getNavigatorValue('language', 'en'),
      languages: getNavigatorValue('languages', []).join(','),
      platform: getNavigatorValue('platform'),
      hardwareConcurrency: getNavigatorValue('hardwareConcurrency', ''),
      deviceMemory: getNavigatorValue('deviceMemory', ''),
      maxTouchPoints: getNavigatorValue('maxTouchPoints', ''),
      cookieEnabled: getNavigatorValue('cookieEnabled', false),
      screenWidth: typeof screen !== 'undefined' ? screen.width : '',
      screenHeight: typeof screen !== 'undefined' ? screen.height : '',
      colorDepth: typeof screen !== 'undefined' ? screen.colorDepth : '',
      pixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
      timezone:
        typeof Intl !== 'undefined'
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : '',
      canvas: getCanvasFingerprint(),
      webgl: getWebGLFingerprint(),
      localStorage: !!storage,
      sessionStorage:
        typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined',
    }
  }

  async createFingerprint() {
    const components = this.collectComponents()
    const hashed = await hashText(JSON.stringify(components))
    return `fp_${hashed.slice(0, 32)}`
  }

  persistFingerprint(fingerprint) {
    const storage = getStorage()
    if (!storage) return

    try {
      storage.setItem(STORAGE_KEYS.fingerprint, fingerprint)
      storage.setItem(STORAGE_KEYS.timestamp, Date.now().toString())
    } catch (error) {
      // Storage can be blocked in private browsing or embedded webviews.
    }
  }

  getCachedFingerprint() {
    const storage = getStorage()
    if (!storage) return this.visitorId

    try {
      return this.visitorId || storage.getItem(STORAGE_KEYS.fingerprint)
    } catch (error) {
      return this.visitorId
    }
  }

  isFingerprintExpired(maxAge = DEFAULT_MAX_AGE) {
    const storage = getStorage()
    if (!storage) return true

    let timestamp = ''

    try {
      timestamp = storage.getItem(STORAGE_KEYS.timestamp)
    } catch (error) {
      return true
    }

    if (!timestamp || Number.isNaN(Number(timestamp))) return true

    return Date.now() - Number(timestamp) > maxAge
  }

  generateFallbackId() {
    const timestamp = Date.now().toString(36)
    const randomStr = Math.random().toString(36).slice(2, 12)
    const userAgent = getNavigatorValue('userAgent').replace(/[^a-zA-Z0-9]/g, '').slice(0, 12)

    return `fp_${timestamp}_${randomStr}_${userAgent}`.toLowerCase()
  }

  async getFingerprint() {
    const cachedFingerprint = this.getCachedFingerprint()
    if (cachedFingerprint && !this.isFingerprintExpired()) {
      this.visitorId = cachedFingerprint
      return cachedFingerprint
    }

    if (!this.fingerprintPromise) {
      this.fingerprintPromise = this.createFingerprint()
        .then(fingerprint => {
          this.visitorId = fingerprint
          this.persistFingerprint(fingerprint)
          return fingerprint
        })
        .catch(() => {
          const fallbackId = this.generateFallbackId()
          this.visitorId = fallbackId
          this.persistFingerprint(fallbackId)
          return fallbackId
        })
        .finally(() => {
          this.fingerprintPromise = null
        })
    }

    return this.fingerprintPromise
  }

  async getDetailedFingerprint() {
    const fingerprint = await this.getFingerprint()
    return {
      visitorId: fingerprint,
      components: this.collectComponents(),
      timestamp: Date.now(),
    }
  }

  async refreshFingerprint() {
    this.clearFingerprint()
    return this.getFingerprint()
  }

  clearFingerprint() {
    const storage = getStorage()

    this.visitorId = null
    this.fingerprintPromise = null

    if (!storage) return

    storage.removeItem(STORAGE_KEYS.fingerprint)
    storage.removeItem(STORAGE_KEYS.timestamp)
  }

  isValidFingerprint(fingerprint) {
    return typeof fingerprint === 'string' && /^fp_[a-z0-9_]+$/i.test(fingerprint)
  }

  getStats() {
    const storage = getStorage()
    const timestamp = storage ? storage.getItem(STORAGE_KEYS.timestamp) : null
    const fingerprint = this.getCachedFingerprint()

    return {
      hasFingerprint: !!fingerprint,
      fingerprint,
      isExpired: this.isFingerprintExpired(),
      createdAt: timestamp ? new Date(Number(timestamp)) : null,
      age: timestamp ? Date.now() - Number(timestamp) : null,
    }
  }
}

const fingerprintService = new FingerprintService()

export default fingerprintService
